import { useState } from 'react';

export default function Contact() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || email.length > 500) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4rbskl1jqpb85ajp400', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppOrder = () => {
    const message = encodeURIComponent('Hello EatSpot! I would like to place an order.');
    window.open(`https://wa.me/923140607452?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="bg-teal-700 text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-3xl font-bold mb-6">Get In Touch</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <i className="ri-map-pin-fill text-2xl text-orange-400 mt-1"></i>
                <div>
                  <p className="text-gray-200 font-medium">F-7 Markaz</p>
                  <p className="text-gray-200">Islamabad, Pakistan</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-phone-fill text-2xl text-orange-400"></i>
                <a href="tel:+923140607452" className="text-white hover:text-orange-400 transition-colors">
                  +92 314 0607452
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-mail-fill text-2xl text-orange-400"></i>
                <a href="mailto:info@eatspot.pk" className="text-white hover:text-orange-400 transition-colors">
                  info@eatspot.pk
                </a>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-8">
              <p className="text-gray-200 mb-4 font-medium">Follow Us</p>
              <div className="flex space-x-3">
                <a href="#" className="w-12 h-12 border-2 border-white/30 rounded-full flex items-center justify-center hover:bg-white hover:text-teal-700 transition-all">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 border-2 border-white/30 rounded-full flex items-center justify-center hover:bg-white hover:text-teal-700 transition-all">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 border-2 border-white/30 rounded-full flex items-center justify-center hover:bg-white hover:text-teal-700 transition-all">
                  <i className="ri-tiktok-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Map */}
          <div>
            <h3 className="text-3xl font-bold mb-6">Find Us</h3>
            <div className="rounded-2xl overflow-hidden shadow-xl h-[280px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3318.5847392!2d73.0461!3d33.7294!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38dfbf1c8c1c1c1d%3A0x1c1c1c1c1c1c1c1c!2sF-7%20Markaz%2C%20Islamabad!5e0!3m2!1sen!2spk!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="EatSpot Location"
              ></iframe>
            </div>
            <a 
              href="https://www.google.com/maps/dir//F-7+Markaz,+Islamabad"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 mt-4 text-orange-400 hover:text-orange-300 transition-colors"
            >
              <span>Get Directions</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          {/* Hours & Quick Order */}
          <div>
            <h3 className="text-3xl font-bold mb-6">Opening Hours</h3>
            <div className="space-y-3 mb-8">
              <div className="flex justify-between">
                <span className="text-gray-200">Monday - Thursday</span>
                <span className="text-white font-medium">11:00 AM - 11:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-200">Friday - Sunday</span>
                <span className="text-white font-medium">10:00 AM - 12:00 AM</span>
              </div>
            </div>

            <div className="h-px bg-white/20 my-6"></div>

            {/* Quick Order */}
            <h4 className="text-xl font-bold mb-4">Quick Order</h4>
            <button
              onClick={handleWhatsAppOrder}
              className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-semibold transition-all hover:scale-105 flex items-center justify-center space-x-2 mb-6 whitespace-nowrap"
            >
              <i className="ri-whatsapp-fill text-2xl"></i>
              <span>Order on WhatsApp</span>
            </button>

            {/* Newsletter */}
            <h4 className="text-xl font-bold mb-4">Newsletter</h4>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form id="newsletter_subscription">
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  maxLength={500}
                  required
                  className="flex-1 px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Subscribe'}
                </button>
              </div>
              {submitMessage && (
                <p className={`mt-2 text-sm ${submitMessage.includes('Thank you') ? 'text-green-300' : 'text-red-300'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-gray-300">Made with ❤️ in Islamabad</p>
            <p className="text-gray-300">© 2025 EatSpot. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-gray-300 hover:text-white transition-colors">Privacy Policy</a>
              <span className="text-gray-500">|</span>
              <a href="#" className="text-gray-300 hover:text-white transition-colors">Terms</a>
              <span className="text-gray-500">|</span>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
